import Tool, { type ToolArgs } from '../../../tools/Tool';
import { Transforms, type Editor, type Element } from 'slate';
import { ReactEditor } from 'slate-react';
import { isLinkCardElement } from '../../../types/guards';
import type { Placement } from '@floating-ui/dom';
import mediaAlignmentLeft from '../../../assets/icons/mediaAlignmentLeft.svg';
import mediaAlignmentCenter from '../../../assets/icons/mediaAlignmentCenter.svg';
import mediaAlignmentRight from '../../../assets/icons/mediaAlignmentRight.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import type { LinkCardAlignment } from '../../../types/aha-editor';
import LinkCardCommon from '../LinkCardCommon';

const alignmentIcons = {
  left: mediaAlignmentLeft,
  center: mediaAlignmentCenter,
  right: mediaAlignmentRight,
};

function updateAlignment(
  editor: Editor | undefined,
  element: Element | undefined,
  value: LinkCardAlignment
) {
  if (!editor || !element || !value || !isLinkCardElement(element)) return;
  const path = ReactEditor.findPath(editor, element);
  if (!path) return;
  Transforms.setNodes(
    editor,
    {
      linkCard_alignment: value,
    },
    {
      at: path,
    }
  );

  // Store the alignment preference for future LinkCard insertions
  localStorage.setItem(LinkCardCommon.linkCardAlignmentPrefKey, value);
}

class ToolLinkCardAlignmentTool extends Tool {
  static toolName = 'Edit alignment';
  static alignmentValue: LinkCardAlignment = 'left';
  static linkCardIcon = mediaAlignmentLeft;

  static tooltip(): string {
    return this.toolName;
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(this.linkCardIcon);
  }

  static action({ editor, element, controller }: ToolArgs): void {
    updateAlignment(editor, element, this.alignmentValue);
  }

  static isActive({ element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;
    return element.linkCard_alignment === this.alignmentValue;
  }
}

class ToolLinkCardAlignLeft extends ToolLinkCardAlignmentTool {
  static toolName = 'Align left';
  static alignmentValue: LinkCardAlignment = 'left';
  static linkCardIcon = mediaAlignmentLeft;
}

class ToolLinkCardAlignCenter extends ToolLinkCardAlignmentTool {
  static toolName = 'Align center';
  static alignmentValue: LinkCardAlignment = 'center';
  static linkCardIcon = mediaAlignmentCenter;
}

class ToolLinkCardAlignRight extends ToolLinkCardAlignmentTool {
  static toolName = 'Align right';
  static alignmentValue: LinkCardAlignment = 'right';
  static linkCardIcon = mediaAlignmentRight;
}

class ToolLinkCardAlignment extends Tool {
  static toolName = 'Text alignment';
  static withoutRefocus = true;

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    const alignment =
      element && isLinkCardElement(element)
        ? element.linkCard_alignment ?? 'left'
        : 'left';
    const mediaAlignmentIcon = alignmentIcons[alignment];
    return svgToIcon(mediaAlignmentIcon);
  }

  static submenuTools = () => [
    [ToolLinkCardAlignLeft, ToolLinkCardAlignCenter, ToolLinkCardAlignRight],
  ];

  static tooltipPlacement(): Placement {
    return 'bottom';
  }
}

export default ToolLinkCardAlignment;
